import React, {PropTypes} from 'react';
import {connect} from 'react-redux';

import * as _ from 'underscore';

import { amountValidation } from '../../../utils/itemValidation';

import RhinoLoading from '../common/RhinoLoading';
import RhinoRadio from '../../common/inputs/RhinoRadio';

import * as financeActionCreators from '../../../actions/finance';
let allActionCreators = Object.assign({}, financeActionCreators);


class FinanceCalculator extends React.Component {

  constructor(props) {
    super(props);

    this.INPUT_TOTAL = 'total';
    this.INPUT_PAYMENT = 'payment';

    this.state = {
      
      requestPending: true,
      requestPlans: [],

      inputMode: this.INPUT_TOTAL,
      
      inputAmount: '',
      inputCost: '',
      inputDown: '',
      inputTax: '',

      errorAmount: null,
      errorCost: null,
      errorDown: null,
      errorTax: null,

      calcAmount: 0,
      calcCost: 0,
      calcDown: 0,
      calcTax: 0,

      resultMin: null,
      resultMax: null,

      width: window.innerWidth,
    };

    this.controller = null;

    this.checkWidthThrottled = _.throttle(this.checkWidth.bind(this), 50);
  }

  componentDidMount() {
    this.fetchPlans();
    window.addEventListener('resize', this.checkWidthThrottled, false);
  }

  componentWillUnmount() {
    if(this.controller) {
      this.controller.abort();
    }
    window.removeEventListener('resize', this.checkWidthThrottled, false);
  }

  checkWidth() {
    this.setState({ width: window.innerWidth });
  }

  showCosts() {
    return !this.props.auth.hideData;
  }

  changeMode(mode) {
    this.setState({ 
      inputMode: mode,
      calcAmount: 0,
      calcCost: 0,
      calcDown: 0,
      calcTax: 0,
    });
  }

  changeAmount(evt) {
    this.setState({ inputAmount: evt.target.value }, () => {
      if(this.state.errorAmount) {
        this.validateAmount();
      }
    });
  }

  validateAmount() {
    this.setState({ errorAmount: amountValidation(this.state.inputAmount) });
  }

  changeCost(evt) {
    this.setState({ inputCost: evt.target.value }, () => {
      if(this.state.errorCost) {
        this.validateCost();
      }
    });
  }

  validateCost() {
    this.setState({ errorCost: amountValidation(this.state.inputCost, true) });
  }

  changeDown(evt) {
    this.setState({ inputDown: evt.target.value }, () => {
      if(this.state.errorDown) {
        this.validateDown();
      }
    });
  }

  validateDown() {
    this.setState({ errorDown: amountValidation(this.state.inputDown, true) });
  }

  changeTax(evt) {
    this.setState({ inputTax: evt.target.value }, () => {
      if(this.state.errorTax) {
        this.validateTax();
      }
    });
  }

  validateTax() {
    this.setState({ errorTax: amountValidation(this.state.inputTax, true) });
  }

  async fetchPlans() {

    if(this.controller) {
      this.controller.abort();
    }
    const controller = new AbortController();
    this.controller = controller;

    this.setState({
      requestPending: true,
      requestPlans: [],
    });

    const plansResp = await this.props.financeFetchPlans(controller.signal)
      .catch((errResp) => {
        
        if(controller.signal.aborted) { return null; }

        console.error(errResp);
        this.setState({ 
          requestPending: false,
          responseObject: [],
        });
      });

    if(!plansResp) { return null; }

    this.setState({
      requestPending: false,
      requestPlans: plansResp || [],
    });
  }

  validateAll() {
    const errorObj = {
      errorAmount: amountValidation(this.state.inputAmount)
    };
    this.setState(errorObj);
    for(const key in errorObj) {
      if(errorObj[key]) {
        return false;
      }
    }
    return true;
  }

  handleSubmit(evt) {
    if(evt) {
      evt.preventDefault();
    }
    if(this.validateAll()) {
      this.setState({ 
        calcAmount: parseFloat(this.state.inputAmount) || 0,
        calcCost: parseFloat(this.state.inputCost) || 0,
        calcDown: parseFloat(this.state.inputDown) || 0,
        calcTax: parseFloat(this.state.inputTax) || 0,
      })
    }
  }

  getPrinciple(plan) {
    const total = this.state.inputMode === this.INPUT_TOTAL ? parseFloat(this.state.calcAmount) : parseFloat(this.state.calcAmount * plan.term);
    return total*(1 + (this.state.calcTax / 100)) - this.state.calcDown;
  }

  getPlanCost(plan) {
    if(!this.state.calcAmount) {
      return '-';
    }
    if(this.showCosts()) {
      const principle = this.getPrinciple(plan);
      return `$${(principle * (plan.cost / 100) + this.state.calcCost).toFixed(2).toLocaleString('en-US')}`;
    }
    return '';
  }

  getCostValue(plan) {
    if(!this.state.calcAmount) {
      return 0;
    }
    if(this.showCosts()) {
      const principle = this.getPrinciple(plan);
      return parseFloat(principle * (plan.cost / 100) + this.state.calcCost) || 0;
    }
    return 0;
  }

  getPlanGp(plan) {
    if(!this.state.calcAmount) {
      return '-';
    }
    if(this.showCosts()) {
      const total = this.state.inputMode === this.INPUT_TOTAL ? parseFloat(this.state.calcAmount) : parseFloat(this.state.calcAmount * plan.term);
      return `${(100*((total - this.getCostValue(plan)) / total)).toFixed(2).toLocaleString('en-US')}%`;
    }
    return '';
  }

  calculatePaymentMonthly(plan) {
    const principle = this.getPrinciple(plan);
    if(plan.apr > 0) {
      const monthlyRate = plan.apr / 100 / 12;
      const monthlyPayment = principle * monthlyRate / (1 - Math.pow(1 + monthlyRate, -plan.term));
      return monthlyPayment;
    } else {
      return principle / plan.term;
    }
  }

  calculatePaymentDaily(plan) {
    const principle = this.getPrinciple(plan);
    if (plan.apr > 0) {
      const dailyRate = plan.apr / 100 / 365;
      // Calculate the equivalent monthly rate from the daily rate
      const monthlyRate = Math.pow(1 + dailyRate, 30) - 1;
      const monthlyPayment = principle * monthlyRate / (1 - Math.pow(1 + monthlyRate, -plan.term));
      return monthlyPayment;
    } else {
      return principle / plan.term;
    }
  }

  calculatePaymentContinuous(plan) {
    const principle = this.getPrinciple(plan);
    if(plan.apr > 0) {
      const monthlyRate = Math.exp(plan.apr / 100 / 12) - 1;
      const monthlyPayment = principle * monthlyRate / (1 - Math.pow(1 + monthlyRate, -plan.term));
      return monthlyPayment;
    } else {
      return principle / plan.term;
    }
  }

  calculateTotalMonthly(plan) {
    const principle = this.getPrinciple(plan);
    if(plan.apr > 0) {
      const monthlyRate = plan.apr / 100 / 12;
      const totalAmount = principle * ((Math.pow(1 + monthlyRate, plan.term) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, plan.term)));
      return totalAmount;
    } else {
      return principle * plan.term;
    }
  }

  calculateTotalDaily(plan) {
    const principle = this.getPrinciple(plan);
    if (plan.apr > 0) {
      const dailyRate = plan.apr / 100 / 365;
      // Calculate the equivalent monthly rate from the daily rate
      const monthlyRate = Math.pow(1 + dailyRate, 30) - 1;
      const totalDays = plan.term * 30; // Approximating each month as 30 days
      const totalAmount = principle * ((Math.pow(1 + monthlyRate, plan.term) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, plan.term)));
      return totalAmount;
    } else {
      return principle * plan.term;
    }
  }

  calculateTotalContinuous(plan) {
    const principle = this.getPrinciple(plan);
    if(plan.apr > 0) {
      const monthlyRate = Math.exp(plan.apr / 100 / 12) - 1;
      const totalAmount = principle * ((Math.pow(1 + monthlyRate, plan.term) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, plan.term)));
      return totalAmount;
    } else {
      return principle * plan.term;
    }
  }

  getCalcResult(plan) {
    if(!this.state.calcAmount) {
      return '-';
    }
    if(this.state.inputMode === this.INPUT_TOTAL) {
      return this.calculatePaymentMonthly(plan);
      // return this.calculatePaymentDaily(plan);
      // return this.calculatePaymentContinuous(plan);
    } else {
      return this.calculateTotalMonthly(plan);
      // return this.calculateTotalDaily(plan);
      // return this.calculateTotalContinuous(plan);
    }
  }

  formatPrice(val, decimalPlaces = 2) {
    if(!val || !parseFloat(val)) { return '-'; }

    return val.toLocaleString('en-US', {
      style: 'decimal',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    });
  }

  getMinResult() {
    if(!this.state.calcAmount) { return ''; }
    const values = [];
    for(const plan of this.state.requestPlans) {
      const val = this.getCalcResult(plan);
      if(val) {
        values.push(val);
      }
    }
    return values.length ? Math.min(...values) : '';
  }

  getMaxResult() {
    if(!this.state.calcAmount) { return ''; }
    const values = [];
    for(const plan of this.state.requestPlans) {
      const val = this.getCalcResult(plan);
      if(val) {
        values.push(val);
      }
    }
    return values.length ? Math.max(...values) : '';
  }
    
  render() {
    return <div className='FinanceCalculator'>
      <div className='calcWrapper'>
        <div className='calcTitle'>Financing Calculator</div>
        <div className='calcBr'>
          <img src='/img/manageusers/iconline_thinlight2.svg' alt='' />
        </div>
        <form className='calcForm' onSubmit={this.handleSubmit.bind(this)}>
          <div className='calcModeWrapper'>
            <div className='calcModes'>
              <div className='modeWrapper'>
                <div className='modeInput'>
                  <RhinoRadio 
                    name='mode' 
                    selected={this.state.inputMode === this.INPUT_TOTAL}
                    onChange={() => this.changeMode(this.INPUT_TOTAL)} />
                </div>
                <div className='modelLabel'>Calculate from jewelry price</div>
              </div>
              <div className='modeWrapper'>
                <div className='modeInput'>
                  <RhinoRadio 
                    name='mode' 
                    selected={this.state.inputMode === this.INPUT_PAYMENT}
                    onChange={() => this.changeMode(this.INPUT_PAYMENT)} />
                </div>
                <div className='modelLabel'>Calculate from payment amount</div>
              </div>
            </div>
          </div>
          <div className='calcInputWrapper'>
            <div className='calcInputRowWrapper'>
              <div className='calcLabelWrapper'>
                <div className='calcLabelValue'>{this.state.inputMode === this.INPUT_TOTAL ? 'Total Amount (Pre tax)': 'Payment Amount'}</div>
              </div>
              <div className='calcInputElementWrapper'>
                <input 
                  type='text'
                  className={`calcInput ${this.state.errorAmount ? 'error' : ''}`}
                  placeholder='Enter amount'
                  value={this.state.inputAmount}
                  onChange={this.changeAmount.bind(this)}
                  onBlur={this.validateAmount.bind(this)} />
                <div className='symbolOverlay'>$</div>
                {this.state.errorAmount ?
                  <div className='calcInputError'>{this.state.errorAmount}</div> :
                  null
                }
              </div>
            </div>
            {this.showCosts() ?
              <div className='calcInputRowWrapper'>
                <div className='calcLabelWrapper'>
                  <div className='calcLabelValue'>Cost</div>
                </div>
                <div className='calcInputElementWrapper'>
                  <input 
                    type='text'
                    className={`calcInput ${this.state.errorCost ? 'error' : ''}`}
                    placeholder='Enter cost'
                    value={this.state.inputCost}
                    onChange={this.changeCost.bind(this)}
                    onBlur={this.validateCost.bind(this)} />
                  <div className='symbolOverlay'>$</div>
                  {this.state.errorCost ?
                    <div className='calcInputError'>{this.state.errorCost}</div> :
                    null
                  }
                </div>
              </div> :
              null
            }
            <div className='calcInputRowWrapper'>
              <div className='calcLabelWrapper'>
                <div className='calcLabelValue'>Down Payment</div>
              </div>
              <div className='calcInputElementWrapper'>
                <input 
                  type='text'
                  className={`calcInput ${this.state.errorDown ? 'error' : ''}`}
                  placeholder='Enter down payment'
                  value={this.state.inputDown}
                  onChange={this.changeDown.bind(this)}
                  onBlur={this.validateDown.bind(this)} />
                <div className='symbolOverlay'>$</div>
                {this.state.errorDown ?
                  <div className='calcInputError'>{this.state.errorDown}</div> :
                  null
                }
              </div>
            </div>
            <div className='calcInputRowWrapper'>
              <div className='calcLabelWrapper'>
                <div className='calcLabelValue'>Sales Tax</div>
              </div>
              <div className='calcInputElementWrapper'>
                <input 
                  type='text'
                  className={`calcInput ${this.state.errorTax ? 'error' : ''}`}
                  placeholder='Sales tax'
                  value={this.state.inputTax}
                  onChange={this.changeTax.bind(this)}
                  onBlur={this.validateTax.bind(this)} />
                <div className='percentOverlay'>%</div>
                {this.state.errorTax ?
                  <div className='calcInputError'>{this.state.errorTax}</div> :
                  null
                }
              </div>
            </div>
            <div className='calcSubmitWrapper'>
              <button type='submit' className='calcSubmitButton'>Calculate</button>
            </div>
          </div>
        </form>
        <div className='calcResultsWrapper'>
          {this.state.requestPending ?
            <div className='calcResultsLoading'>
              <RhinoLoading message='Loading financing plans' />
            </div> :
            <div className='calcLoaded'>
              <div className={`calcResultTitleWrapper ${this.state.calcAmount ? 'open' : ''}`}>
                <div className={`calcResultTitle`}>
                  <div className='calcResultTitleLabel'>{this.state.inputMode === this.INPUT_TOTAL ? 'Estimated payment range' : 'Estimated price range'}:</div>
                  <div className='calcResultTitleValue'>{`$${this.formatPrice(this.getMinResult(), 0)} - ${this.formatPrice(this.getMaxResult(), 0)}`}</div>
                </div>
              </div>
              <div className={'calcTableWrapper'}>
                <div className={`calcTable ${this.showCosts() ? 'costs' : ''}`}>
                  <div className='tableHeaderWrapper'>
                    <div className='tableHeader'>
                      <div className={'idCell headerCell'}>
                        <div className='headerValue'>Plan</div>
                      </div>
                      <div className={'termCell headerCell'}>
                        <div className='headerValue'>Term</div>
                      </div>
                      <div className={'aprCell headerCell'}>
                        <div className='headerValue'>APR</div>
                      </div>
                      <div className={'costCell headerCell'} style={this.showCosts() ? {} : { display: 'none' }}>
                        <div className='headerValue'>Cost</div>
                      </div>
                      <div className={'gpCell headerCell'} style={this.showCosts() ? {} : { display: 'none' }}>
                        <div className='headerValue'>GP %</div>
                      </div>
                      <div className={'paymentCell headerCell'}>
                        <div className='headerValue'>{this.state.inputMode === this.INPUT_TOTAL ? 'Payment' : 'Total'}</div>
                      </div>
                    </div>
                  </div>
                  <div className='tableBodyWrapper'>
                    <div className='tableBody'>
                      {this.state.requestPlans.map((plan, i) => {
                        return <div key={i} className='tableRowWrapper'>
                          <div className='tableRow'>
                            <div className={'idCell bodyCell'}>
                              <div className='bodyValue'>{`${plan.plan_id}`}</div>
                            </div>
                            <div className={'termCell bodyCell'}>
                              <div className='bodyValue valueDesktop'>{`${plan.term} months`}</div>
                              <div className='bodyValue valueMobile'>{`${plan.term}`}</div>
                            </div>
                            <div className={'aprCell bodyCell'}>
                              <div className='bodyValue'>{`${plan.apr}%`}</div>
                            </div>
                            <div className={'costCell bodyCell'} style={this.showCosts() ? {} : { display: 'none' }}>
                              <div className='bodyValue'>{`${this.getPlanCost(plan)}`}</div>
                            </div>
                            <div className={'gpCell bodyCell'} style={this.showCosts() ? {} : { display: 'none' }}>
                              <div className='bodyValue'>{`${this.getPlanGp(plan)}`}</div>
                            </div>
                            <div className={'paymentCell bodyCell'}>
                              <div className='bodyValue'>{this.state.calcAmount ? `$${this.formatPrice(this.getCalcResult(plan))}` : `-`}</div>
                            </div>
                          </div>
                        </div>
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>;
  }
}
export default FinanceCalculator;

function mapStateToProps(state) {
  return {
    auth: state.auth,
    common: state.common,
  };
}

export const FinanceCalculatorContainer = connect(
  mapStateToProps,
  allActionCreators
)(FinanceCalculator);